import type { ErrorResponse } from '@connaro/shared';

/** Error thrown for any non-2xx API response, carrying the unified error shape. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'connaro_token';
let token: string | null = localStorage.getItem(TOKEN_KEY);

export function setToken(next: string | null): void {
  token = next;
  if (next) localStorage.setItem(TOKEN_KEY, next);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return token;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'accept-language': navigator.language };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return undefined as T;

  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ErrorResponse | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'internal',
      err?.message ?? 'Request failed',
      err?.requestId,
      err?.details,
    );
  }
  return data as T;
}

export interface Me {
  id: string;
  kind: string;
  userId: string | null;
  tenantId: string;
  displayName: string;
  isPlatformAdmin: boolean;
  permissions: string[];
}

export interface LoginResult {
  token: string;
  expiresAt: string;
  user: { id: string; email: string; displayName: string; tenantId: string };
  /** True when the deployment enforces MFA and this user has not enrolled. */
  mfaSetupRequired?: boolean;
}

/** Returned by login when the password is correct but a second factor is required. */
export interface MfaChallenge {
  mfaRequired: true;
  challenge: string;
}

export type LoginResponse = LoginResult | MfaChallenge;

export function isMfaChallenge(r: LoginResponse): r is MfaChallenge {
  return 'mfaRequired' in r && r.mfaRequired;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: (path: string, body?: unknown) => request<void>('DELETE', path, body),
  login: (tenantSlug: string, email: string, password: string) =>
    request<LoginResponse>('POST', '/auth/login', { tenantSlug, email, password }),
  verifyMfaLogin: (challenge: string, code: string) =>
    request<LoginResult>('POST', '/auth/mfa/verify', { challenge, code }),
  me: () => request<Me>('GET', '/auth/me'),
};
